import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, CheckCircle } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError('Something went wrong. Please try again or contact us via WhatsApp.');
    }
    setLoading(false);
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-white pt-20">
      {/* Header */}
      <div className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="font-body text-sm tracking-[0.2em] uppercase text-gold font-semibold mb-3">Get in Touch</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">Contact Us</h1>
          <p className="font-body text-base text-gray-400 max-w-xl mx-auto">Have a question or ready to plan your trip? We'd love to hear from you.</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Form */}
          <div>
            <h2 className="font-heading text-2xl font-bold text-navy mb-6">Send Us a Message</h2>

            {success ? (
              <div data-testid="contact-success" className="flex flex-col items-center justify-center p-10 rounded-3xl bg-green-50 border border-green-100 text-center">
                <CheckCircle className="w-16 h-16 text-green-500 mb-4" />
                <h3 className="font-heading text-xl font-semibold text-green-800 mb-2">Message Sent!</h3>
                <p className="font-body text-sm text-green-700">Thank you! Our team will get back to you within 24 hours.</p>
                <button onClick={() => setSuccess(false)} className="mt-4 font-body text-sm text-gold hover:underline">Send another message</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                <div>
                  <label className="font-body text-sm font-medium text-navy mb-1 block">Full Name</label>
                  <input type="text" required value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                    data-testid="contact-name" placeholder="Your name"
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-body text-sm font-medium text-navy mb-1 block">Email</label>
                    <input type="email" required value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                      data-testid="contact-email" placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-2xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" />
                  </div>
                  <div>
                    <label className="font-body text-sm font-medium text-navy mb-1 block">Phone</label>
                    <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                      data-testid="contact-phone" placeholder="+91 XXXXX XXXXX"
                      className="w-full px-4 py-3 rounded-2xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors" />
                  </div>
                </div>
                <div>
                  <label className="font-body text-sm font-medium text-navy mb-1 block">Message</label>
                  <textarea rows={5} required value={form.message} onChange={e => setForm({...form, message: e.target.value})}
                    data-testid="contact-message" placeholder="Tell us about your travel plans..."
                    className="w-full px-4 py-3 rounded-2xl border border-gray-200 font-body text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors resize-none" />
                </div>
                {error && <p className="font-body text-sm text-red-500">{error}</p>}
                <button type="submit" disabled={loading} data-testid="contact-submit"
                  className="flex items-center justify-center gap-2 w-full bg-gold text-white hover:bg-gold-dark rounded-full py-3.5 font-body font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50">
                  {loading ? 'Sending...' : <><Send className="w-4 h-4" /> Send Message</>}
                </button>
              </form>
            )}
          </div>

          {/* Info */}
          <div className="space-y-8">
            <div>
              <h2 className="font-heading text-2xl font-bold text-navy mb-6">Contact Information</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-navy-50 border border-gray-100">
                  <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center shrink-0"><Phone className="w-5 h-5 text-gold" /></div>
                  <div>
                    <h4 className="font-body text-sm font-semibold text-navy mb-1">Phone / WhatsApp</h4>
                    <a href="tel:+919179992213" className="font-body text-sm text-gray-500 hover:text-gold transition-colors block">+91 9179992213</a>
                    <a href="tel:+917999017544" className="font-body text-sm text-gray-500 hover:text-gold transition-colors block">+91 7999017544</a>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-navy-50 border border-gray-100">
                  <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-gold" /></div>
                  <div>
                    <h4 className="font-body text-sm font-semibold text-navy mb-1">Email</h4>
                    <a href="mailto:admin@beyond-horizon.co.in" className="font-body text-sm text-gray-500 hover:text-gold transition-colors">admin@beyond-horizon.co.in</a>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-5 rounded-2xl bg-navy-50 border border-gray-100">
                  <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center shrink-0"><MapPin className="w-5 h-5 text-gold" /></div>
                  <div>
                    <h4 className="font-body text-sm font-semibold text-navy mb-1">Office Address</h4>
                    <p className="font-body text-sm text-gray-500">2nd Floor Dream Towers, Kushkal Pur Chowk, Raipur — 492001</p>
                  </div>
                </div>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a href="https://wa.me/919179992213?text=Hi!%20I%20want%20to%20plan%20a%20trip%20with%20Beyond%20Horizons."
              target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp"
              className="flex items-center gap-3 p-5 rounded-2xl bg-green-50 border border-green-100 hover:bg-green-100 transition-colors">
              <svg viewBox="0 0 24 24" className="w-8 h-8 fill-green-500 shrink-0"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              <div>
                <h4 className="font-body text-sm font-semibold text-green-800">Prefer WhatsApp?</h4>
                <p className="font-body text-xs text-green-600">Chat with us directly for instant assistance</p>
              </div>
            </a>

            {/* Map */}
            <div className="rounded-3xl overflow-hidden h-64 border border-gray-100">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3718.2!2d81.6296!3d21.2514!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a28dda23be28229%3A0x4288ee6e46f26620!2sRaipur%2C%20Chhattisgarh!5e0!3m2!1sen!2sin!4v1234567890"
                width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy"
                title="Beyond Horizons Office Location"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
